import React from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import { List, ListItem } from '@mobi/component-library/Base/List'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { AppRoutes } from '@core/AppRoutes'
import { OverlayClose } from '@core/Components/Overlay/signals'
import { appHistory } from '@core/Utils/routing/history'
import {
  selectPrevRacingSearchTerms,
  selectPrevSportsSearchTerms,
} from '@core/Areas/Search/Store/selectors'
import type { SearchModeType } from '@core/Areas/Search/types'
import { clearAllPreviousSearchTerms, removeSearchTermFromPrevious } from '@core/Areas/Search/Store'

export const EmptyScreen: React.FC<{
  searchMode: SearchModeType
  handleImmediateSearch: (searchTerm: string) => void
}> = ({ searchMode, handleImmediateSearch }) => {
  const dispatch = useDispatch()
  const prevSportsSearchTerms = useSelector(selectPrevSportsSearchTerms)
  const prevRacingSearchTerms = useSelector(selectPrevRacingSearchTerms)

  const isRacingMode = searchMode === 'racing'
  const prevSearchTerms = isRacingMode ? prevRacingSearchTerms : prevSportsSearchTerms

  if (prevSearchTerms.length > 0) {
    return (
      <Flex flexDirection='column' gap={spacing.md}>
        <List
          variant='card'
          title='Recent Searches'
          titleRightAlignedContent={
            <Button
              size='xs'
              isIconOnlyButton
              color='tertiary_color'
              data-testid='Search.ClearAll'
              onClick={() => dispatch(clearAllPreviousSearchTerms({ type: searchMode }))}
            >
              Clear All
            </Button>
          }
        >
          {prevSearchTerms.map(searchTerm => (
            <ListItem
              key={searchTerm}
              text={searchTerm}
              testId='Search.Previous'
              onClick={() => handleImmediateSearch(searchTerm)}
              contentRight={
                <Button
                  size='xs'
                  isIconOnlyButton
                  color='tertiary_grey'
                  data-testid='Search.RemovePrevious'
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    dispatch(removeSearchTermFromPrevious({ type: searchMode, searchTerm }))
                  }}
                >
                  <Icon name='SolidXClose' size='2.4rem' />
                </Button>
              }
            />
          ))}
        </List>

        {isRacingMode && (
          <Flex justifyContent='center'>
            <Button size='sm' color='primary' onClick={onAdvancedSearchClick}>
              Advanced Search <Icon name='DuocolorFilterFunnel01' size='2rem' />
            </Button>
          </Flex>
        )}
      </Flex>
    )
  }

  return (
    <WrapperStyled>
      <div>
        <Icon name='DuocolorSearchSm' size='6.4rem' color={colors.studio[500]} />
      </div>

      <div>
        <span>Start Your Search</span>

        {isRacingMode ? (
          <span>Search for runners, jockeys, races and more</span>
        ) : (
          <span>Search for events, teams, players or competitions</span>
        )}
      </div>

      {isRacingMode && (
        <Button size='sm' color='primary' onClick={onAdvancedSearchClick}>
          Advanced Search <Icon name='DuocolorFilterFunnel01' size='2rem' />
        </Button>
      )}
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

const onAdvancedSearchClick = () => {
  OverlayClose()
  appHistory.push(AppRoutes.StructuredSearch)
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.md,
  paddingTop: '4rem',

  // Icon
  '> div:nth-of-type(1)': {
    display: 'grid',
    placeContent: 'center',
    width: '10.4rem',
    height: '10.4rem',
    background: colors.studio[100],
    borderRadius: radius.full,
  },

  // Text
  '> div:nth-of-type(2)': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx2,
    fontFamily: font.family.primary,
    textAlign: 'center',
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    maxWidth: '20rem',

    '> span:first-of-type': { fontWeight: font.weight.semibold },
    '> span:last-of-type': { fontWeight: font.weight.regular },
  },
})
